// src/data/seatRecord.js
//
// SEATS OF RECORD BEFORE ACCOUNTS (Record 690; Bob 2026-10-14, "Address, fix and finish 3-6", suggestion 5). A term of
// office is by ACCOUNT (setOfficeHolder: "a term is by account; create the account first"), so a Local Chairperson who has
// not yet signed up cannot be put on the record at all - every LC seat reads NOT OF RECORD (ruling 54) and stays so until
// the GCs onboard (Record 689 OPEN). Ruling 54.3: "Who sits in the seat is a data-entry task with a deadline of L." This is
// that data entry: the committee's General Chairperson (or the administrator) records WHO HOLDS a seat - name, e-mail,
// from when, how, on what evidence - with no account needed. It is a RECORD, not a term: it grants nothing, routes nothing,
// and gives nobody a token. When that person's account exists, the General Chairperson seats him from it (setOfficeHolder,
// prefilled from the record), and the record is marked seated with the term's id.
//
// Who may record: exactly who may seat (officeSuccession.mayAttestSeat) - the administrator for any office; a General
// Chairperson for the Local Chairperson seats of his own committee. Who may read: the administrator and that committee's
// General Chairperson (firestore.rules seatRecords) - ten real names were the PII worry of ruling 54.4; they are not
// readable by every signed-in account the way officeTerms are.
// Pure. The callable recordSeatHolder (functions\index.js) decides with this module (projected to functions\shared\).
import { mayAttestSeat, METHODS } from './officeSuccession';

const s = (v) => (v === undefined || v === null ? '' : String(v).trim());
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v)) && !isNaN(Date.parse(s(v) + 'T00:00:00Z'));
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const SEAT_RECORD_STATUS = ['recorded', 'seated', 'withdrawn'];

// The committee an office belongs to: an LC seat's parent; the General office is its own.
export function officeCommittee(office) {
  if (!office) return '';
  return office.type === 'general_committee' ? s(office.officeId) : s(office.parentOffice);
}

// Checks a record before anything writes it. input: { holderName, holderEmail, startedOn, method, evidence }.
export function validateSeatRecord(input, office, caller, todayIso) {
  const i = input || {};
  const errors = [];
  if (!office) return { ok: false, errors: ['that office is not in the offices registry'] };
  const may = mayAttestSeat(caller, office);
  if (!may.ok) return { ok: false, errors: [may.reason] };
  if (!s(i.holderName)) errors.push('the holder\'s name is required');
  if (!EMAIL.test(s(i.holderEmail).toLowerCase())) errors.push('the holder\'s e-mail is required - it is how his account is matched when he signs up');
  if (!isDate(i.startedOn)) errors.push('the day the term began is required (a fact, not a default)');
  else if (todayIso && s(i.startedOn) > s(todayIso).slice(0, 10)) errors.push('the term cannot begin in the future');
  if (METHODS.indexOf(s(i.method)) === -1) errors.push('how he came to hold it must be one of: ' + METHODS.join(', '));
  if (!s(i.evidence)) errors.push('the evidence is required (the election result, the appointment letter, the roster it comes from)');
  if (s(i.holderName).length > 120 || s(i.evidence).length > 500) errors.push('the name is at most 120 characters and the evidence 500');
  return { ok: errors.length === 0, errors, as: may.as };
}

// The record the server writes (the server adds recordedAt from its own clock).
export function buildSeatRecord(input, office, caller, prior) {
  const i = input || {};
  const entry = {
    holderName: s(i.holderName), holderEmail: s(i.holderEmail).toLowerCase(), startedOn: s(i.startedOn), method: s(i.method),
    evidence: s(i.evidence), recordedBy: s(caller && caller.uid), recordedByName: s(caller && (caller.name || caller.email)),
  };
  const history = (prior && Array.isArray(prior.history) ? prior.history : []).slice(-19);
  if (prior && prior.holderEmail) history.push({ holderName: prior.holderName, holderEmail: prior.holderEmail, startedOn: prior.startedOn, status: prior.status, recordedByName: prior.recordedByName || '' });
  return { officeId: s(office.officeId), committee: officeCommittee(office), label: s(office.label || office.officeId), status: 'recorded', ...entry, termId: '', accountNoticedAt: '', history };
}

// What a seat shows. A held term outranks any record (the term is the fact); a record is shown only while the seat has no
// holder of record and the record is still 'recorded'.
export function seatRecordLine(record, officeState) {
  if (!record || s(record.status) !== 'recorded') return null;
  if (officeState && officeState.state === 'held') return null;
  return record.holderName + ' (' + record.holderEmail + '), from ' + record.startedOn + ', ' + s(record.method).replace('_', ' ')
    + ' - recorded by ' + (record.recordedByName || 'unknown') + '. No platform account is linked yet: the seat is not held on the platform until he is seated.';
}

// The form a seat-from-record prefills (SeatsPanel's Seat form, which calls setOfficeHolder).
export function seatFormFrom(record) {
  return { officeId: s(record && record.officeId), holderEmail: s(record && record.holderEmail), holderName: s(record && record.holderName),
    startedOn: s(record && record.startedOn), method: s(record && record.method) || 'elected', evidence: s(record && record.evidence), endReason: 'term_end' };
}
